import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {TabContext, TabPanel} from "@mui/lab";
import {Box, Stack, Tab, TextField, Typography} from "@mui/material";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import DetailsLoading from "crqlar-react-common/dist/components/DetailsLoading";
import Property from "crqlar-react-common/dist/components/Property";
import ScrollableTabList from "crqlar-react-common/dist/components/tabs/ScrollableTabList";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {BaseModel} from "crqlar-react-common/dist/models/common/base.model";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useTranslation} from "react-i18next";
import {useQueryClient} from "react-query";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {resolveTab} from "src/utils/tab.util";
import SettingsEntityHeader from "../../system/layout/SettingsEntityHeader";
import DigitalSigningDesignFields from "./DigitalSigningDesignFields";
import DigitalSigningFontFields from "./DigitalSigningFontFields";
import DigitalSigningLegalFields from "./DigitalSigningLegalFields";
import DigitalSigningOfferField from "./DigitalSigningOfferField";
import {SETTINGS_ROWS_GAP, SettingsSection, SettingsSectionDivider} from "./DigitalSigningSettingsSection";
import {DIGITAL_SIGNING_TEMPLATES_PATH, DigitalSigningTemplate} from "./digitalSigningTemplate.model";
import {DigitalSigningTemplateService} from "./digitalSigningTemplate.service";
import {
    formFromTemplate,
    DigitalSigningTemplateForm,
    hasBasicInfoErrors,
    hasTemplateFormErrors,
    hasTextsErrors,
    hasTipErrors,
    toSaveRequest,
    validateTemplateForm
} from "./digitalSigningTemplate.util";
import DigitalSigningThankYouFields from "./DigitalSigningThankYouFields";
import DigitalSigningTipEditor from "./DigitalSigningTipEditor";
import DigitalSigningTipServices from "./DigitalSigningTipServices";
import DigitalSigningTipTextFields from "./DigitalSigningTipTextFields";
import DigitalSigningTitleFields from "./DigitalSigningTitleFields";
import {invalidateDigitalSigningTemplateChoices} from "../digitalSigningQueries";

interface TemplateEntity extends BaseModel, DigitalSigningTemplateForm {
    saved: DigitalSigningTemplate;
}

const TABS = ['basic', 'tip', 'texts'];

const TAB_SX = {minHeight: 48};
const PANEL_SX = {p: 0};

const DigitalSigningTemplatePage = () => {
    useTrackScreenVisit('system digital signing template');

    const {t, i18n} = useTranslation();
    const {id} = useParams();
    const navigate = useNavigate();
    const readOnly = useReadOnly();
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = resolveTab(searchParams.get('tab'), TABS, 'basic');
    const language = i18n.language?.toLowerCase().startsWith('de') ? 'de' : 'en';

    const toEntity = (saved: DigitalSigningTemplate): TemplateEntity => (
        {id: saved.id, ...formFromTemplate(saved, language), saved}
    );
    const save = async (entity: TemplateEntity) => toEntity(await DigitalSigningTemplateService.update(toSaveRequest(entity, entity.saved)));
    const templateChanged = () => void invalidateDigitalSigningTemplateChoices(queryClient);

    const data = useEntity<TemplateEntity>({
        get: async () => toEntity(await DigitalSigningTemplateService.get(id!)),
        update: save,
        create: save,
        delete: templateId => DigitalSigningTemplateService.delete([templateId]),
        onDeleted: () => {
            templateChanged();
            navigate(DIGITAL_SIGNING_TEMPLATES_PATH, {replace: true});
        },
        isValid: entity => !!entity.tip && !hasTemplateFormErrors(validateTemplateForm(entity)),
        onSaved: templateChanged,
    }, id);

    const entity = data.item;
    const setEntity = (patch: Partial<TemplateEntity>) => data.setItem(current => ({...current, ...patch}));

    const header = (
        <SettingsEntityHeader
            title={entity.name}
            data={data}
            back={() => navigate(DIGITAL_SIGNING_TEMPLATES_PATH)}
        />
    );

    if (!entity.tip || !entity.saved) {
        return (
            <Box sx={{width: '100%', pb: 8}}>
                {header}
                {data.loading && <DetailsLoading/>}
            </Box>
        );
    }

    const errors = validateTemplateForm(entity);
    const errorIcon = (show: boolean) => (
        show ? <ErrorOutlineIcon color={'error'} titleAccess={t('Has errors')} sx={{fontSize: 18}}/> : undefined
    );

    const tabsSection = (
        <ScrollableTabList onChange={(_event, tab) => setSearchParams({tab})}>
            <Tab label={t('Basic info')} value={'basic'} icon={errorIcon(hasBasicInfoErrors(errors))} iconPosition={'end'} sx={TAB_SX}/>
            <Tab label={t('Tip')} value={'tip'} icon={errorIcon(hasTipErrors(errors))} iconPosition={'end'} sx={TAB_SX}/>
            <Tab label={t('Texts')} value={'texts'} icon={errorIcon(hasTextsErrors(errors))} iconPosition={'end'} sx={TAB_SX}/>
        </ScrollableTabList>
    );

    const basicInfoTab = (
        <Stack gap={6}>
            <SettingsSection title={t('Basic info')}>
                <Stack gap={SETTINGS_ROWS_GAP}>
                    <Property title={t('Name')} tooltip={t('Staff see it when they create a link; the guest never does.')}>
                        <TextField
                            fullWidth
                            size={'small'}
                            value={entity.name}
                            onChange={event => setEntity({name: event.target.value})}
                            error={!!errors.name}
                            helperText={errors.name && t(errors.name)}
                            disabled={readOnly}
                            inputProps={{'aria-label': t('Name')}}
                        />
                    </Property>
                    <Property title={t('Sectors')} tooltip={t('Folios of reservations in these sectors can use this template. Empty: offered nowhere.')}>
                        <DigitalSigningOfferField
                            value={entity.scopedSectorIds}
                            onChange={scopedSectorIds => setEntity({scopedSectorIds})}
                            disabled={readOnly}
                        />
                    </Property>
                </Stack>
            </SettingsSection>
            <SettingsSectionDivider/>

            <SettingsSection title={t('Design')}>
                <DigitalSigningDesignFields
                    design={entity.design}
                    onChange={design => setEntity({design})}
                    errors={errors}
                    disabled={readOnly}
                />
            </SettingsSection>
            <SettingsSectionDivider/>

            <SettingsSection title={t('Font')}>
                <DigitalSigningFontFields
                    value={entity.design}
                    onChange={font => setEntity({design: {...entity.design, ...font}})}
                    errors={errors}
                    disabled={readOnly}
                />
            </SettingsSection>
            <SettingsSectionDivider/>

            <SettingsSection title={t('Legal')}>
                <DigitalSigningLegalFields
                    value={{privacyPolicyUrl: entity.privacyPolicyUrl, imprintUrl: entity.imprintUrl}}
                    onChange={links => setEntity(links)}
                    errors={errors}
                    disabled={readOnly}
                />
            </SettingsSection>
            <SettingsSectionDivider/>

            <Stack gap={{xs: 2.5, sm: 2}}>
                <DangerZone
                    title={t('Delete template')}
                    description={t('New links can no longer be created with it; the links issued with it keep their page.')}
                    buttonLabel={t('Delete')}
                    loading={data.saving}
                    onDelete={() => data.removeNow()}
                />
                <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                    {t('Last updated')}: {entity.saved.updateDate?.toLocaleString() || '-'}
                </Typography>
            </Stack>
        </Stack>
    );

    const tipTab = (
        <Stack gap={6}>
            <DigitalSigningTipEditor
                tip={entity.tip}
                errors={errors}
                onChange={tip => setEntity({tip})}
                currency={entity.saved.currency}
                language={language}
                preselectAllowed={entity.saved.preselectAllowed}
                disabled={readOnly}
            />

            {entity.tip.enabled && (
                <>
                    <SettingsSectionDivider/>
                    <SettingsSection title={t('Who gets the tip')}>
                        <DigitalSigningTipServices offeredSectorIds={entity.scopedSectorIds}/>
                    </SettingsSection>
                </>
            )}
        </Stack>
    );

    const textsTab = (
        <Stack gap={6}>
            <SettingsSection title={t('Title')}>
                <DigitalSigningTitleFields
                    value={{titles: entity.titles, subtitles: entity.subtitles}}
                    onChange={texts => setEntity(texts)}
                    errors={errors}
                    disabled={readOnly}
                />
            </SettingsSection>
            <SettingsSectionDivider/>

            <SettingsSection title={t('Thank-you message')}>
                <DigitalSigningThankYouFields
                    value={entity.thankYouMessages}
                    onChange={thankYouMessages => setEntity({thankYouMessages})}
                    errors={errors.thankYouMessages}
                    disabled={readOnly}
                />
            </SettingsSection>

            {entity.tip.enabled && (
                <>
                    <SettingsSectionDivider/>
                    <SettingsSection title={t('Tip')}>
                        <DigitalSigningTipTextFields
                            value={{labels: entity.tip.labels, descriptions: entity.tip.descriptions}}
                            onChange={texts => setEntity({tip: {...entity.tip, ...texts}})}
                            errors={errors}
                            disabled={readOnly}
                        />
                    </SettingsSection>
                </>
            )}
        </Stack>
    );

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {header}
            <TabContext value={activeTab}>
                <Stack sx={{width: '100%', maxWidth: '900px', mx: 'auto', mt: 3, gap: 6}}>
                    {tabsSection}
                    <TabPanel value={'basic'} sx={PANEL_SX}>{basicInfoTab}</TabPanel>
                    <TabPanel value={'tip'} sx={PANEL_SX}>{tipTab}</TabPanel>
                    <TabPanel value={'texts'} sx={PANEL_SX}>{textsTab}</TabPanel>
                </Stack>
            </TabContext>
        </Box>
    );
};

export default DigitalSigningTemplatePage;
